import {
    Card,
    CardContent,
    CardHeader,
    Button,
    Divider,
    Typography,
    InputAdornment,
    Link,
    IconButton,
    Unstable_Grid2 as Grid,
    Box,
} from '@mui/material';
import { FormTextField } from '@/components/HookForm';
import { useForm } from 'react-hook-form';
import { LoadingButton } from '@/components/common/LoadingButton';
import {
    Lock as LockIcon,
    Visibility as ShowIcon,
    VisibilityOff as HideIcon,
    Close as CloseIcon,
} from '@mui/icons-material';

import { apiUrls, services } from '@/constant/configure';
import { signInSession, getUserData, getOwnMalls } from '@/services/auth.service';
import { getSpaceUserData } from '@/services/studio.service';
import { toast } from 'react-toastify';
import { useState } from 'react';
import { dialogOpen, dialogClose } from '@/modules/MuiDialog';
import ResetPassword from '@/pages/dialogs/ResetPassword';
import sessionStore from '@/store/modules/sessionStore';
import spaceUserV2Store from '@/store/modules/spaceUserV2Store';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import socialKakao from '@/assets/images/sources/social_logo/logo_kakao_btn.png';
import socialNaver from '@/assets/images/sources/social_logo/logo_naver_btn.png';

interface LoginDialogProps {
    onLoginSuccess?: () => void;
}

export default function LoginDialog({ onLoginSuccess }: LoginDialogProps) {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const form = useForm();
    const {
        handleSubmit,
        getValues,
        formState: { isValid, isSubmitting },
    } = form;

    const [isPassView, setIsPassView] = useState(false);

    const signIn = async (data: any) => {
        try {
            if (!isValid || isSubmitting) return;
            const email = data.email.trim();
            const password = data.password;

            // 1. signInSession 내부에서 setSession을 호출하여 sessionStore에 저장
            const session = await signInSession(email, password);

            // 2. 추가 사용자 데이터 로드 (userStore, spaceUserStore, spaceUserV2Store 업데이트)
            try {
                await Promise.all([getUserData(session.userId), getSpaceUserData(session.userId)]);
            } catch (err) {
                console.warn('사용자 추가 정보 로드 실패:', err);
            }

            // 3. 몰 정보 로드 시도 (몰이 있는 경우 mallId 업데이트)
            try {
                const { persistenceMallId } = await getOwnMalls(session.accessToken);
                if (persistenceMallId) {
                    const { setSession } = sessionStore.getState();
                    setSession({
                        ...session,
                        mallId: persistenceMallId,
                    });
                }
            } catch (err) {
                console.warn('몰 정보 로드 실패 (몰이 개설되지 않았을 수 있음):', err);
            }

            toast.success('로그인되었습니다.');

            // 다이얼로그 닫기 - sessionStore가 업데이트되면 자동으로 UI가 반영됨
            dialogClose();

            // 4. 소도몰 역할 확인 및 리다이렉트
            const { grade_name } = spaceUserV2Store.getState();
            const isSodomall = grade_name === '소도몰';

            // 로그인 성공 콜백 실행 (선택적)
            if (onLoginSuccess) {
                onLoginSuccess();
            } else if (isSodomall) {
                // 소도몰 역할이고 콜백이 없으면 프로필링크 페이지로 리다이렉트
                navigate(`/studio/${session.userId}`);
            }
        } catch (e: any) {
            console.error(e);

            if (!!e.data?.placeHolder || !!e.placeHolder) {
                toast.error(e.data?.placeHolder || e.placeHolder);
            } else {
                if (e.status === 302) {
                    toast.error('비활성화 된 계정입니다.');
                    return;
                }

                toast.error('로그인이 실패하였습니다.');
                console.error(e);
            }
        }
    };

    const { social } = services;
    const { kakao_client_id, kakao_redirect_url, naver_client_id, naver_redirect_url } = social;
    const naverHeaderUrl = 'https://nid.naver.com/oauth2.0/authorize';
    const kakaoHeaderUrl = 'https://kauth.kakao.com/oauth/authorize';

    const currentPath = window.location.pathname;
    const naverURL = `${naverHeaderUrl}?response_type=code&client_id=${naver_client_id}&redirect_uri=${naver_redirect_url}&state=${currentPath}`;
    const kakaoURL = `${kakaoHeaderUrl}?response_type=code&client_id=${kakao_client_id}&redirect_uri=${kakao_redirect_url}&state=${currentPath}`;

    const socialLoginList: { text: string; color: any; href: string; img: any }[] = [
        { text: 'kakao', color: 'warning', href: kakaoURL, img: socialKakao },
        { text: 'naver', color: 'success', href: naverURL, img: socialNaver },
    ];

    return (
        <Card
            elevation={0}
            sx={{
                width: '100%',
                maxWidth: 420,
                py: 1,
                px: 1,
                borderRadius: `20px`,
                position: 'relative',
            }}
        >
            <CardHeader
                title={t('auth.login')}
                sx={{
                    pb: 1,
                    pt: 4,
                    px: 2,
                    textAlign: 'center',
                    '& .MuiCardHeader-title': {
                        textAlign: 'center',
                        fontSize: '1.5rem',
                        fontWeight: 600,
                    },
                }}
                action={
                    <IconButton
                        onClick={() => dialogClose()}
                        sx={{
                            position: 'absolute',
                            right: 8,
                            top: 8,
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                }
            />
            <CardContent sx={{ px: 2, py: 1 }}>
                <form onSubmit={handleSubmit(signIn)}>
                    <FormTextField
                        label={t('auth.email')}
                        placeholder={'account@email.com'}
                        name={'email'}
                        form={form}
                        rules={[(v) => !!v || t('auth.emailRequired')]}
                        fullWidth
                    />
                    <FormTextField
                        label={t('auth.password')}
                        name={'password'}
                        form={form}
                        rules={[(v) => !!v || t('auth.passwordRequired')]}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position={'end'}>
                                    <IconButton
                                        onClick={() => {
                                            setIsPassView(!isPassView);
                                        }}
                                    >
                                        {isPassView ? <HideIcon /> : <ShowIcon />}
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                        type={isPassView ? 'text' : 'password'}
                        fullWidth
                    />

                    <Button
                        color={'primary'}
                        variant={'contained'}
                        size={'large'}
                        type={'submit'}
                        disabled={isSubmitting}
                        fullWidth
                    >
                        {t('auth.login')}
                    </Button>
                    <div style={{ textAlign: 'right' }}>
                        <Button
                            color={'primary'}
                            size={'small'}
                            sx={{ mt: 1 }}
                            startIcon={<LockIcon />}
                            onClick={() => {
                                dialogOpen({
                                    innerElement: <ResetPassword email={getValues('email')} />,
                                });
                            }}
                        >
                            {t('auth.forgotPassword')}
                        </Button>
                    </div>
                </form>
                <Divider sx={{ my: 2 }} />
                <div className={'text-center mt-5'}>
                    <Typography variant="body2" component="div">
                        {t('auth.notDdmMember')}{' '}
                        <Link
                            href={`/register/sign-up`}
                            color="primary"
                            sx={{ fontWeight: 500, cursor: 'pointer' }}
                            onClick={() => {
                                dialogClose();
                            }}
                        >
                            {t('auth.register')}
                        </Link>
                    </Typography>
                </div>
                <Grid container>
                    <Grid xs={12} className="flex justify-center mt-5 gap-4">
                        {socialLoginList.map((item, i) => {
                            return (
                                <a key={item.text + 'icon' + i} className="cursor-pointer" href={item.href}>
                                    <img
                                        src={item.img}
                                        style={{
                                            aspectRatio: '1',
                                            width: '50px',
                                        }}
                                        alt={`${item.text} ${t('auth.login')}`}
                                    />
                                </a>
                            );
                        })}
                    </Grid>
                </Grid>
            </CardContent>
        </Card>
    );
}
